import { useHandler } from "@redotech/react-util/hook";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import {
  SegmentPropertyMatchMode,
  segmentPropertyMatchMode,
} from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { OperatorSelector } from "@redotech/redo-web/conversion/operator-selector";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";

interface Property {
  key: string;
  value: string;
  matchMode?: SegmentPropertyMatchMode;
}

const matchModeToText: Record<SegmentPropertyMatchMode, string> = {
  exact: "Exact",
  wildcard: "Wildcard",
};

export const PropertySelectionModal = memo(function PropertySelectionModal({
  selectedProperties,
  onSave,
  onClose,
}: {
  selectedProperties: Property[];
  onSave: (properties: Property[]) => void;
  onClose: () => void;
}) {
  const [properties, setProperties] = useState<Property[]>(
    selectedProperties.length > 0
      ? selectedProperties
      : [{ key: "", value: "" }],
  );

  const handlePropertyChange = useHandler(
    (index: number, field: keyof Property, value: string) => {
      setProperties((prev) =>
        prev.map((prop, i) =>
          i === index ? { ...prop, [field]: value } : prop,
        ),
      );
    },
  );

  const handleMatchModeChange = useHandler(
    (index: number, matchMode: SegmentPropertyMatchMode) => {
      setProperties((prev) =>
        prev.map((prop, i) => (i === index ? { ...prop, matchMode } : prop)),
      );
    },
  );

  const handleAddProperty = useHandler(() => {
    setProperties([...properties, { key: "", value: "" }]);
  });

  const handleRemoveProperty = useHandler((index: number) => {
    if (properties.length > 1) {
      const updatedProperties = properties.filter((_, i) => i !== index);
      setProperties(updatedProperties);
    }
  });

  const handleSave = useHandler(() => {
    const validProperties = properties.filter(
      (prop) => prop.key.trim() !== "" && prop.value.trim() !== "",
    );
    onSave(validProperties);
    onClose();
  });

  const isValid = properties.every(
    (prop) => prop.key.trim() !== "" && prop.value.trim() !== "",
  );

  return (
    <RedoModal
      modalSize="md"
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Save", onClick: handleSave, disabled: !isValid }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle="Add key-value pairs to match against properties of items in the cart."
      title="Configure line item properties"
    >
      <Flex dir="column" gap="md" w="full">
        <Text fontSize="sm" fontWeight="medium">
          Properties
        </Text>
        {properties.map((property, index) => {
          return (
            <Flex align="flex-start" dir="row" gap="md" key={index} w="full">
              <Flex align="flex-start" dir="row" flex={1} gap="sm">
                <RedoTextInput
                  fullWidth
                  placeholder="Property key (e.g., _purchase_option)"
                  setValue={(key) => handlePropertyChange(index, "key", key)}
                  value={property.key}
                />
                <Flex align="center" pt="sm">
                  <Text fontSize="sm" fontWeight="medium">
                    :
                  </Text>
                </Flex>
                <RedoTextInput
                  fullWidth
                  placeholder={
                    (property.matchMode ?? "exact") === "wildcard"
                      ? "Wildcard pattern (e.g., sub*)"
                      : "Property value (e.g., subscription)"
                  }
                  setValue={(value) =>
                    handlePropertyChange(index, "value", value)
                  }
                  value={property.value}
                />
                <OperatorSelector<SegmentPropertyMatchMode>
                  availableOptions={[...segmentPropertyMatchMode]}
                  onOperatorSelected={(mode) =>
                    handleMatchModeChange(index, mode)
                  }
                  operatorToText={matchModeToText}
                  selectedOperator={property.matchMode ?? "exact"}
                />
              </Flex>
              <Flex align="center" pt="sm">
                <RedoButton
                  hierarchy="tertiary"
                  IconLeading={TrashIcon}
                  onClick={() => handleRemoveProperty(index)}
                  size="sm"
                  theme="destructive"
                />
              </Flex>
            </Flex>
          );
        })}
        <Flex alignSelf="flex-start">
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={handleAddProperty}
            size="xs"
            text="Add property"
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
